import {Box, Typography} from "@mui/material";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import {useCallback, useLayoutEffect, useRef, useState} from "react";

const COMPACT_HEIGHT = 180;
const PERSPECTIVE_DISTANCE = 800;

function compactRotation(imageHeight: number) {
    if (imageHeight <= COMPACT_HEIGHT) {
        return 0;
    }

    let lowerBound = 0;
    let upperBound = Math.PI / 2;

    for (let iteration = 0; iteration < 20; iteration++) {
        const rotation = (lowerBound + upperBound) / 2;
        const projectedHeight = imageHeight * Math.cos(rotation) * PERSPECTIVE_DISTANCE
            / (PERSPECTIVE_DISTANCE + imageHeight * Math.sin(rotation));

        if (projectedHeight > COMPACT_HEIGHT) {
            lowerBound = rotation;
        } else {
            upperBound = rotation;
        }
    }

    return upperBound * 180 / Math.PI;
}

export function FloorPlanImage({src, alt, compact = false}: {
    src?: string | null;
    alt: string;
    compact?: boolean;
}) {
    const imageRef = useRef<HTMLImageElement>(null);
    const [compactRotationDegrees, setCompactRotationDegrees] = useState(0);
    const updateCompactRotation = useCallback(() => {
        if (compact && imageRef.current) {
            setCompactRotationDegrees(compactRotation(imageRef.current.clientHeight));
        }
    }, [compact]);

    useLayoutEffect(() => {
        if (!compact || !imageRef.current) {
            return;
        }

        const image = imageRef.current;
        const resizeObserver = new ResizeObserver(updateCompactRotation);
        resizeObserver.observe(image);
        updateCompactRotation();
        return () => resizeObserver.disconnect();
    }, [compact, src, updateCompactRotation]);

    if (!src) {
        return (
            <Box sx={{
                height: compact ? COMPACT_HEIGHT : 360,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                bgcolor: 'action.hover',
                color: 'text.secondary',
            }}>
                <MapOutlinedIcon fontSize="large"/>
                <Typography variant="body2">No floor plan available</Typography>
            </Box>
        );
    }

    if (compact) {
        return (
            <Box sx={{
                position: 'relative',
                height: COMPACT_HEIGHT,
                overflow: 'hidden',
                bgcolor: 'background.paper',
            }}>
                <Box
                    ref={imageRef}
                    component="img"
                    src={src}
                    alt={alt}
                    onLoad={updateCompactRotation}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        bottom: 0,
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        transform: `perspective(${PERSPECTIVE_DISTANCE}px) rotateX(${compactRotationDegrees}deg)`,
                        transformOrigin: 'center bottom',
                        backfaceVisibility: 'hidden',
                    }}
                />
            </Box>
        );
    }

    return (
        <Box
            ref={imageRef}
            component="img"
            src={src}
            alt={alt}
            sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                maxHeight: 'calc(100vh - 180px)',
                objectFit: 'contain',
                bgcolor: 'background.paper',
            }}
        />
    );
}
